import { ErrorHandler, Injectable, inject } from '@angular/core';
import type { SupabaseClient } from '@supabase/supabase-js';
import { CapabilityDefectReporter } from '../../../shared/services/capability-defect-reporter.service';
import {
  isRollupVendorKey,
  resolveVendor,
} from '../../../shared/services/vendor-channel-resolver.service';
import { AuthStore } from '../../auth.store';
import { type ChunkProgressReporter, reportChunkProgress } from '../shared/chunk-progress';
import type { EAN } from '../shared/ean';
import { fromMasked, toMasked } from '../shared/masked-ean';
import type {
  AvailabilityChannel,
  AvailabilityDecodeFailure,
  AvailabilityDecodeResult,
  AvailabilityObservation,
  InStoreAvailabilityObservation,
} from './availability';
import {
  type AvailabilityReadResult,
  AvailabilityRepository,
  EMPTY_AVAILABILITY_READ,
} from './availability-repository';

const TABLE = 'portal_distribution';
/** Only what the decoder reads: the largest brand is 28,272 rows of payload. */
const SELECTED_COLUMNS = 'c_key, name, payl';
const PAGE_SIZE = 1000;
const KEY_BATCH_SIZE = 200;
const MAX_CONCURRENT_BATCHES = 5;

/** The only key shape the feed stores a SKU under; every other shape is skipped unparsed. */
const BARE_EAN_KEY = /^\d{6,}$/;
/** Week keys are a week-start DATE (its Monday), not an ISO week label. */
const WEEK_KEY = /^\d{4}-\d{2}-\d{2}$/;

// ---------------------------------------------------------------------------
// Wire decoding -- everything below this line knows the feed's shapes
// ---------------------------------------------------------------------------

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/** What one fact is keyed by, carried through the decode of a single vendor entry. */
type FactKey = { readonly ean: EAN; readonly week: string; readonly vendor: string };

function failure(
  ean: EAN | null,
  week: string | null,
  vendor: string | null,
  reason: string
): AvailabilityDecodeFailure {
  return { kind: 'err', ean, week, vendor, reason };
}

function decodeOnline(entry: Record<string, unknown>, base: FactKey): AvailabilityDecodeResult {
  const flag = entry['in_stock'];
  if (typeof flag === 'boolean') {
    return { kind: 'ok', observation: { ...base, channel: 'online', inStock: flag } };
  }
  if (flag !== undefined && flag !== null) {
    return failure(base.ean, base.week, base.vendor, 'online in-stock flag is not a boolean');
  }
  // Weeks scraped before the flag existed carry only the in-stock listing count.
  const listings = entry['is'];
  if (typeof listings !== 'number' || !Number.isFinite(listings)) {
    return failure(
      base.ean,
      base.week,
      base.vendor,
      'online entry carries neither an in-stock flag nor a numeric listing count'
    );
  }
  return { kind: 'ok', observation: { ...base, channel: 'online', inStock: listings > 0 } };
}

function decodeInStore(entry: Record<string, unknown>, base: FactKey): AvailabilityDecodeResult {
  const notNumbers: string[] = [];

  // Absent stays absent: a 0 here would report "measured, and none" for something the
  // feed never measured.
  const measure = (wireField: string, factField: string): number | undefined => {
    const raw = entry[wireField];
    if (raw === undefined || raw === null) return undefined;
    if (typeof raw !== 'number' || !Number.isFinite(raw)) {
      notNumbers.push(factField);
      return undefined;
    }
    return raw;
  };

  const measures = {
    storeTotal: measure('store_total', 'storeTotal'),
    inStockStoreCount: measure('is_store_count', 'inStockStoreCount'),
    outOfStockStoreCount: measure('oos_store_count', 'outOfStockStoreCount'),
    listedStoreCount: measure('listed_store_count', 'listedStoreCount'),
    inStockShareOfAllStores: measure('is_pct_allstores', 'inStockShareOfAllStores'),
    inStockShareOfListedStores: measure('is_pct_listed', 'inStockShareOfListedStores'),
    zipPrefixCoverage: measure('zip_prefix_coverage_pct', 'zipPrefixCoverage'),
    zipPrefixConcentration: measure('zip_prefix_concentration', 'zipPrefixConcentration'),
  };

  if (notNumbers.length > 0) {
    return failure(
      base.ean,
      base.week,
      base.vendor,
      `in-store measures are not numbers: ${notNumbers.join(', ')}`
    );
  }

  const observation: InStoreAvailabilityObservation = { ...base, channel: 'in-store', ...measures };
  return { kind: 'ok', observation };
}

/** One payload section: the section name, not the vendor key, decides the channel. */
function decodeSection(
  rawSection: unknown,
  channel: AvailabilityChannel,
  ean: EAN,
  week: string
): readonly AvailabilityDecodeResult[] {
  if (rawSection === undefined || rawSection === null) return [];
  const section = asRecord(rawSection);
  if (!section) return [failure(ean, week, null, `${channel} section is not an object`)];

  const rawVendors = section['by_vendor'];
  if (rawVendors === undefined || rawVendors === null) return [];
  const vendors = asRecord(rawVendors);
  if (!vendors) return [failure(ean, week, null, `${channel} vendor map is not an object`)];

  const results: AvailabilityDecodeResult[] = [];
  for (const [key, rawEntry] of Object.entries(vendors)) {
    if (isRollupVendorKey(key)) continue;
    const { vendor } = resolveVendor(key);
    const entry = asRecord(rawEntry);
    if (!entry) {
      results.push(failure(ean, week, vendor, `${channel} vendor entry is not an object`));
      continue;
    }
    const base: FactKey = { ean, week, vendor };
    results.push(channel === 'online' ? decodeOnline(entry, base) : decodeInStore(entry, base));
  }
  return results;
}

type RowDecode = {
  /** null when the row's own key did not parse as an EAN. */
  readonly ean: EAN | null;
  readonly name: string | null;
  readonly results: readonly AvailabilityDecodeResult[];
};

/** Returns null for a row whose key is not a SKU key at all (brand aggregates and friends). */
function decodeRow(row: Record<string, unknown>): RowDecode | null {
  const rawKey = row['c_key'];
  if (typeof rawKey !== 'string' || !BARE_EAN_KEY.test(rawKey)) return null;

  const rawName = row['name'];
  const name = typeof rawName === 'string' ? rawName : null;

  // Every SKU key the feed stores is masked, so a bare-digit key that is not one is a
  // contract violation, reported as a decode failure rather than read as a real code.
  const ean = fromMasked(rawKey);
  if (!ean) {
    return {
      ean: null,
      name,
      results: [failure(null, null, null, `product key is not a masked EAN: "${rawKey}"`)],
    };
  }

  const payload = asRecord(row['payl']);
  if (!payload) {
    return { ean, name, results: [failure(ean, null, null, 'payload is not an object')] };
  }
  const weeks = asRecord(payload['weeks']);
  if (!weeks) {
    return { ean, name, results: [failure(ean, null, null, 'payload carries no weeks object')] };
  }

  const results: AvailabilityDecodeResult[] = [];
  for (const [week, rawWeek] of Object.entries(weeks)) {
    if (!WEEK_KEY.test(week)) {
      results.push(failure(ean, week, null, 'week key is not a YYYY-MM-DD date'));
      continue;
    }
    const weekRecord = asRecord(rawWeek);
    if (!weekRecord) {
      results.push(failure(ean, week, null, 'week entry is not an object'));
      continue;
    }
    results.push(
      ...decodeSection(weekRecord['online'], 'online', ean, week),
      ...decodeSection(weekRecord['retail'], 'in-store', ean, week)
    );
  }
  return { ean, name, results };
}

/** Streams rather than materialises: `receptbelagt` decodes 28k rows x 8 weeks per read. */
function* decodedFacts(
  results: readonly AvailabilityDecodeResult[]
): Iterable<AvailabilityObservation> {
  for (const result of results) {
    if (result.kind === 'ok') yield result.observation;
  }
}

function chunk<T>(values: readonly T[], size: number): readonly (readonly T[])[] {
  const chunks: T[][] = [];
  for (let i = 0; i < values.length; i += size) {
    chunks.push(values.slice(i, i + size));
  }
  return chunks;
}

// ---------------------------------------------------------------------------
// Supabase implementation
// ---------------------------------------------------------------------------

@Injectable()
export class SupabaseAvailabilityRepository extends AvailabilityRepository {
  private readonly authStore = inject(AuthStore);
  private readonly errorHandler = inject(ErrorHandler);
  private readonly defectReporter = inject(CapabilityDefectReporter);

  async readByBrand(brandSlug: string): Promise<AvailabilityReadResult> {
    const supabase = await this.authStore.getSupabaseClient();
    const rows = await this.readPages(supabase, brandSlug, null);
    return rows === null ? EMPTY_AVAILABILITY_READ : this.decodeAndReport(rows);
  }

  async readByEans(
    eans: readonly EAN[],
    brandSlug: string,
    onProgress?: ChunkProgressReporter
  ): Promise<AvailabilityReadResult> {
    if (eans.length === 0) return EMPTY_AVAILABILITY_READ;

    const supabase = await this.authStore.getSupabaseClient();
    const batches = chunk(eans.map(toMasked), KEY_BATCH_SIZE);
    const rows: Record<string, unknown>[] = [];

    for (let i = 0; i < batches.length; i += MAX_CONCURRENT_BATCHES) {
      const slice = batches.slice(i, i + MAX_CONCURRENT_BATCHES);
      const wave = await Promise.all(
        slice.map((batch) => this.readPages(supabase, brandSlug, batch))
      );
      reportChunkProgress(onProgress, (i + slice.length) * KEY_BATCH_SIZE, eans.length);
      // One failed batch makes the whole answer unknowable; a partial set would read
      // as "these SKUs are not listed anywhere".
      for (const batchRows of wave) {
        if (batchRows === null) return EMPTY_AVAILABILITY_READ;
        rows.push(...batchRows);
      }
    }
    return this.decodeAndReport(rows);
  }

  /** Pages with .range() until a short page. Returns null once a page has errored. */
  private async readPages(
    supabase: SupabaseClient,
    brandSlug: string,
    keys: readonly string[] | null
  ): Promise<Record<string, unknown>[] | null> {
    const rows: Record<string, unknown>[] = [];
    let offset = 0;

    for (;;) {
      let query = supabase.from(TABLE).select(SELECTED_COLUMNS).eq('brand', brandSlug);
      if (keys) query = query.in('c_key', keys);

      const { data, error } = await query.range(offset, offset + PAGE_SIZE - 1);
      if (error) {
        this.errorHandler.handleError(
          new Error(
            `[AvailabilityRepository] read of ${TABLE} for brand "${brandSlug}" failed: ${error.message}`
          )
        );
        return null;
      }

      const page: readonly Record<string, unknown>[] = data ?? [];
      rows.push(...page);
      if (page.length < PAGE_SIZE) return rows;
      offset += PAGE_SIZE;
    }
  }

  private decodeAndReport(rows: readonly Record<string, unknown>[]): AvailabilityReadResult {
    const observations: AvailabilityDecodeResult[] = [];
    const names = new Map<EAN, string | null>();

    for (const row of rows) {
      const decoded = decodeRow(row);
      if (!decoded) continue;
      observations.push(...decoded.results);
      if (decoded.ean) names.set(decoded.ean, decoded.name);
    }

    this.defectReporter.report(TABLE, decodedFacts(observations));
    return { observations, names };
  }
}
